import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ProductCard } from '../shop/ui/ProductCard';
import { useProducts } from '../shop/core/hooks/useProducts';
import { useCategories } from '../shop/core/hooks/useCategories';
import { Loading } from './ui/Loading';

const Pokemon = () => {
  const navigate = useNavigate();
  const { categories, fetchCategories } = useCategories();
  const [singlesCategoryId, setSinglesCategoryId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Find the Singles category ID
  useEffect(() => {
    // Force refresh to ensure latest taxonomy
    fetchCategories({ forceRefresh: true });
  }, [fetchCategories]);

  useEffect(() => {
    if (categories.length > 0) {
      // Find the parent Singles category (not a child category)
      const singlesCategory = categories.find(cat => {
        const name = String(cat.name || '').toLowerCase();
        const slug = String(cat.slug || '').toLowerCase();
        const isParent = !cat.parent || cat.parent === 0;
        return isParent && (slug === 'singles' || name === 'singles');
      });
      if (singlesCategory) {
        
        setSinglesCategoryId(singlesCategory.id);
      } else {
        console.warn('⚠️ [Singles] Parent Singles category not found');
      }
    }
  }, [categories]);

  const { products: allProducts, loading, error, fetchProducts } = useProducts({ 
    perPage: 50, // fetch broader set; we'll pick top 15 by price
    orderBy: 'date', 
    order: 'desc', 
    page: 1,
  });

  // When Singles category is resolved, fetch top-priced Singles
  useEffect(() => {
    if (singlesCategoryId) {
      fetchProducts({
        page: 1,
        perPage: 50, // broader sample; we will sort client-side by price
        orderBy: 'date',
        order: 'desc',
        category: singlesCategoryId,
      });
    }
  }, [singlesCategoryId, fetchProducts]);

  // Client-side: take top 15 by price from fetched Singles
  const products = React.useMemo(() => {
    if (!allProducts.length) return [];
    const sorted = [...allProducts].sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    return sorted.slice(0, 15);
  }, [allProducts]);

  // Carousel navigation functions
  const nextSlide = () => {
    const maxIndex = Math.max(0, Math.ceil(products.length / 4) - 1);
    if (currentIndex < maxIndex) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Get current slide products (4 at a time)
  const getCurrentSlideProducts = () => {
    const startIndex = currentIndex * 4;
    return products.slice(startIndex, startIndex + 4);
  };

  const maxSlides = Math.ceil(products.length / 4);

  return (
    <section className="gears singles-section shop-page" id="singles">
      <div className="container">
        <h2 className="h2 section-title">Singles</h2>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loading size="lg" text="Loading singles..." />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">Error loading products: {error}</p>
          </div>
        ) : (
          <div className="relative">
            {/* Product Grid */}
            <div className="grid gap-2 sm:gap-6 grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {getCurrentSlideProducts().map((product) => (
                <ProductCard 
                  key={product.id}
                  product={product} 
                  onViewDetails={(product) => navigate(`/shop/product/${product.id}`)}
                />
              ))}
            </div>
            
            {/* Slide Indicator with Navigation */}
            {maxSlides > 1 && (
              <div className="flex justify-center items-center mt-6 gap-4">
                <button
                  onClick={prevSlide}
                  className="w-10 h-10 text-white hover:text-tertiary transition-all duration-200 flex items-center justify-center"
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft size={20} />
                </button>
                
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-400 mr-4">
                    Slide {currentIndex + 1} of {maxSlides} ({products.length} products)
                  </div>
                  {Array.from({ length: maxSlides }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-200 ${
                        index === currentIndex 
                          ? 'bg-tertiary' 
                          : 'bg-gray-600 hover:bg-gray-500'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  className="w-10 h-10 text-white hover:text-tertiary transition-all duration-200 flex items-center justify-center"
                  disabled={currentIndex >= maxSlides - 1}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
            
            {/* View All Button */}
            <div className="flex justify-center mt-8">
              <button
                onClick={() => {
                  
                  
                  navigate(`/shop?category=${singlesCategoryId}`);
                }}
                className="btn btn-primary px-8 py-3 text-lg font-semibold"
              >
                View All Singles
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Pokemon;
